/**
 * LA PORTE — ce qu'on voit avant d'avoir un compte.
 *
 * Elle n'a que trois choses à faire : montrer le mot, dire en trois phrases ce qu'est ce jeu (et
 * surtout ce qu'il coûte : on y meurt pour de bon), et laisser entrer. Pas de carrousel, pas de
 * « fonctionnalités » : un livre-jeu se promet en une page.
 *
 * Le cimetière est visible AVANT l'inscription, et c'est délibéré : la promesse de permadeath n'est
 * pas une phrase marketing, c'est une liste de noms.
 */
import { useState, type FormEvent } from "react";

import { useRessource } from "../api/chargement.js";
import type { DefuntEnVue } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Logo } from "../composants/Logo.js";
import { Message } from "../composants/Message.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import { messageDe } from "../lexique/erreurs.js";
import { ARCHETYPES } from "../lexique/mots.js";
import { useSession } from "../session.js";
import { t } from "../lexique/langue.js";

function Serment() {
  return (
    <div className="serment">
      <div className="serment__article">
        <Glyphe nom="plume" taille={26} couleur="var(--or)" />
        <h3>{t("Un monde que personne n'a lu")}</h3>
        <p>
          {t("L'aventure que vous ouvrirez n'existait pas la veille. Aucun guide en ligne ne vous dira où aller : ce que vous saurez de ce monde, vous l'aurez appris en y marchant. Et")}{" "}
          <em>{t("aucune porte n'attend une clef qu'on aurait oublié de poser")}</em> {t("— on s'en assure avant d'en écrire la première phrase.")}
        </p>
      </div>
      <div className="serment__article">
        <Glyphe nom="crane" taille={26} couleur="var(--sang)" />
        <h3>{t("Une seule vie")}</h3>
        <p>
          {t("Votre héros ne deviendra pas plus fort en chemin : il n'aura que ce que vous lui donnerez au départ, et ce qu'il ramassera. S'il meurt, il ne revient pas — son nom passe au cimetière, où n'importe qui peut venir le lire.")}
        </p>
      </div>
      <div className="serment__article">
        <Glyphe nom="des" taille={26} couleur="var(--essence)" />
        <h3>{t("Vous verrez le dé")}</h3>
        <p>
          {t("Le dé roule à l'écran et le chiffre à battre est écrit à côté. Quand un coup vous tue, vous savez lequel et de combien il s'en est fallu — et vous pouvez le faire rejouer à l'identique, pour le montrer à quelqu'un.")}
        </p>
      </div>
    </div>
  );
}

function Guichet() {
  const { connecte, inscris } = useSession();
  const [nouveau, poseNouveau] = useState(false);
  const [email, poseLEmail] = useState("");
  const [motDePasse, poseLeMotDePasse] = useState("");
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState(false);

  async function soumets(evenement: FormEvent) {
    evenement.preventDefault();
    poseLeRefus(null);
    poseEnCours(true);
    try {
      if (nouveau) await inscris(email, motDePasse);
      else await connecte(email, motDePasse);
    } catch (erreur) {
      poseLeRefus(messageDe(erreur));
    } finally {
      poseEnCours(false);
    }
  }

  return (
    <Parchemin variante="leve" className="guichet">
      <h2>{nouveau ? t("Ouvrir un compte") : t("Reprendre le livre")}</h2>
      <p className="douce" style={{ marginTop: "var(--e2)" }}>
        {nouveau
          ? t("Un compte gratuit tient un héros vivant à la fois. C'est assez pour mourir plusieurs fois.")
          : t("Vos héros vous attendent là où vous les avez laissés.")}
      </p>

      <form className="pile" onSubmit={soumets} style={{ marginTop: "var(--e5)" }}>
        <label className="champ">
          <span className="capitales">{t("Adresse")}</span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => poseLEmail(e.target.value)}
            placeholder={t("vous@exemple.fr")}
          />
        </label>
        <label className="champ">
          <span className="capitales">{t("Mot de passe")}</span>
          <input
            type="password"
            autoComplete={nouveau ? "new-password" : "current-password"}
            required
            minLength={nouveau ? 10 : undefined}
            value={motDePasse}
            onChange={(e) => poseLeMotDePasse(e.target.value)}
            placeholder={nouveau ? t("dix caractères au moins") : ""}
          />
        </label>

        {refus ? <Message espece="refus">{refus}</Message> : null}

        <button type="submit" className="bouton bouton--grand" disabled={enCours}>
          {enCours ? t("Un instant…") : nouveau ? t("Ouvrir le compte") : t("Entrer")}
        </button>
        <button type="button" className="bouton bouton--discret" onClick={() => poseNouveau((n) => !n)}>
          {nouveau ? t("J'ai déjà un compte") : t("Je n'ai pas encore de compte")}
        </button>
      </form>
    </Parchemin>
  );
}

function DerniersMorts() {
  const { donnees } = useRessource<DefuntEnVue[]>("/public/cimetiere", (charge: { heros: DefuntEnVue[] }) => charge.heros);
  const morts = (donnees ?? []).slice(0, 6);
  if (!morts.length) return null;

  return (
    <section className="pile" style={{ marginTop: "var(--e8)" }}>
      <Filet>{t("Tombés récemment")}</Filet>
      <ul className="epitaphes">
        {morts.map((mort, index) => (
          <li key={`${mort.nom}-${index}`}>
            <span className="epitaphes__nom">{mort.nom}</span>
            <span className="faible">
              {ARCHETYPES[mort.archetype]?.nom ?? mort.archetype} — {mort.cause ?? "cause inconnue"}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Porte() {
  return (
    <div className="page arrivee">
      <div className="frontispice">
        <Logo devise={t("Le livre dont on ne sort qu'une fois")} eclat hauteur={190} />
        <p className="frontispice__accroche">
          {t("Vous ouvrez un livre que personne n'a lu avant vous. Vous avancez de page en page, vous notez sur la carte ce que vous avez vu, et vous décidez seul — sans reprise, sans partie sauvegardée juste avant, sans seconde chance.")}
        </p>
      </div>

      <Serment />

      <div className="porte__guichet">
        <Guichet />
      </div>

      <DerniersMorts />
    </div>
  );
}
